import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import { useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  AutocompleteRenderGetTagProps,
  Button,
  Chip,
  Container,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import {
  Controller,
  FieldError,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CampaignGiftType,
  ChannelType,
  InstagramContentType,
  TaskItemType,
  TiktokContentType,
  YoutubeContentType,
} from '@social-garden/utils/types';
import { useTranslation } from 'react-i18next';
import {
  assertUnreachable,
  getUriWithHash,
  getUTCStartOfDay,
  onChangeHelper,
} from '@social-garden/utils/helpers.ts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { mapTaskItemToTaskItemInput } from '@social-garden/utils/taskItem.ts';
import { formatISO } from 'date-fns';
import {
  campaignDocument,
  updateCampaignDocument,
  updateCampaignHeaderImageDocument,
} from '@social-garden/api/documents/campaign.ts';
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { CampaignRewardType } from '@social-garden/api/gql/graphql.ts';
import {
  UpdateCampaignFieldValues,
  UpdateCampaignSchema,
} from '@/constants/ValidationSchema.ts';
import ImageCropField from '@/components/ImageCropField.tsx';
import CampaignNameField from '@/components/CampaignNameField.tsx';
import CampaignDescriptionField from '@/components/CampaignDescriptionField.tsx';
import CategoryField from '@/components/CategoryField.tsx';
import AddressField from '@/components/AddressField.tsx';
import GiftNameField from '@/components/GiftNameField.tsx';
import GiftDescriptionField from '@/components/GiftDescriptionField.tsx';
import RewardTypeField from '@/components/RewardTypeField.tsx';
import ApplyUntilField from '@/components/ApplyUntilField.tsx';
import FixedMoneyRewardField from '@/components/FixedMoneyRewardField.tsx';
import { FormFieldProps } from '@/utils/types.ts';
import Section from '@/components/Section.tsx';
import ChannelTypeField from '@/components/ChannelTypeField.tsx';
import PageTitle from '@/components/PageTitle.tsx';
import GiftTypeField from '@/components/GiftTypeField.tsx';
import InstagramContentTypeField from '@/components/InstagramContentTypeField.tsx';
import YoutubeContentTypeField from '@/components/YoutubeContentTypeField.tsx';
import TiktokContentTypeField from '@/components/TiktokContentTypeField.tsx';
import IncludeContentFileOnSubmissionField from '@/components/IncludeContentFileOnSubmissionField.tsx';
import BrandField from '@/components/BrandField.tsx';
import usePreventLeavingUnsavedForm from '@/hooks/usePreventLeavingUnsavedForm.ts';
import IsDiscoverableField from '@/components/IsDiscoverableField.tsx';
import TargetCountriesField from '@/components/TargetCountriesField.tsx';

export const Route = createFileRoute('/_manager/_app/campaigns/$id/update')({
  component: UpdateCampaign,
});

function UpdateCampaign() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'manager']);
  const { enqueueSnackbar } = useSnackbar();

  const { data } = useSuspenseQuery(campaignDocument, {
    variables: {
      id,
    },
  });

  const [updateCampaignHeaderImage] = useMutation(
    updateCampaignHeaderImageDocument,
  );
  const [updateCampaign, { loading: updateCampaignLoading }] = useMutation(
    updateCampaignDocument,
  );

  const {
    watch,
    control,
    formState: { isValid, dirtyFields, isSubmitting: isFormSubmitting },
    handleSubmit,
  } = useForm<UpdateCampaignFieldValues>({
    resolver: zodResolver(UpdateCampaignSchema),
    mode: 'all',
    defaultValues: {
      name: data.campaign.name,
      description: data.campaign.description,
      taskItems: data.campaign.taskItems.map(mapTaskItemToTaskItemInput),
      address: data.campaign.address || '',
      gifts: data.campaign.gifts,
      brandId: data.campaign.brand.id,
      category: data.campaign.category,
      targetCountries: data.campaign.targetCountries,
      includeContentFileOnSubmission:
        data.campaign.includeContentFileOnSubmission,
      isDiscoverable: data.campaign.isDiscoverable,
      type: {
        channelType: data.campaign.typeable.channelType,
        instagramCampaign: {
          contentType:
            data.campaign.typeable.__typename === 'InstagramCampaign'
              ? data.campaign.typeable.instagramContentType
              : InstagramContentType.REEL,
        },
        youtubeCampaign: {
          contentType:
            data.campaign.typeable.__typename === 'YoutubeCampaign'
              ? data.campaign.typeable.youtubeContentType
              : YoutubeContentType.VIDEO,
        },
        tiktokCampaign: {
          contentType:
            data.campaign.typeable.__typename === 'TiktokCampaign'
              ? data.campaign.typeable.tiktokContentType
              : TiktokContentType.VIDEO,
        },
      },
      questions: data.campaign.questions,
      reward: {
        rewardType: data.campaign.rewardType,
        fixedMoneyRewardValue: data.campaign.fixedMoneyRewardValue || undefined,
      },
      applyUntil: data.campaign.applyUntil
        ? getUTCStartOfDay(data.campaign.applyUntil)
        : undefined,
    },
  });

  usePreventLeavingUnsavedForm({ dirtyFields, isSubmitting: isFormSubmitting });

  const {
    fields: taskItemFields,
    append: appendTaskItem,
    remove: removeTaskItem,
    update: updateTaskItem,
    move: moveTaskItem,
  } = useFieldArray<UpdateCampaignFieldValues, 'taskItems'>({
    control,
    name: 'taskItems',
  });

  const {
    fields: giftFields,
    append: appendGift,
    remove: removeGift,
  } = useFieldArray<UpdateCampaignFieldValues, 'gifts'>({
    control,
    name: 'gifts',
  });

  const {
    fields: questionFields,
    append: appendQuestion,
    remove: removeQuestion,
    move: moveQuestion,
  } = useFieldArray<UpdateCampaignFieldValues, 'questions'>({
    control,
    name: 'questions',
  });

  const handleOnValid = useCallback(
    async ({
      name,
      description,
      brandId,
      category,
      type,
      taskItems,
      address,
      gifts,
      questions,
      reward,
      applyUntil,
      includeContentFileOnSubmission,
      isDiscoverable,
      targetCountries,
    }: UpdateCampaignFieldValues) => {
      const response = await updateCampaign({
        variables: {
          input: {
            id,
            name,
            description,
            brandId,
            taskItems,
            address,
            gifts,
            category,
            questions,
            type: {
              channelType: type.channelType,
              instagramCampaign:
                type.channelType === ChannelType.INSTAGRAM
                  ? type.instagramCampaign
                  : undefined,
              youtubeCampaign:
                type.channelType === ChannelType.YOUTUBE
                  ? type.youtubeCampaign
                  : undefined,
              tiktokCampaign:
                type.channelType === ChannelType.TIKTOK
                  ? type.tiktokCampaign
                  : undefined,
            },
            rewardType: reward.rewardType,
            fixedMoneyRewardValue:
              reward.rewardType === CampaignRewardType.FIXED_MONEY_REWARD &&
              reward.fixedMoneyRewardValue
                ? reward.fixedMoneyRewardValue
                : undefined,
            applyUntil: applyUntil
              ? formatISO(applyUntil, { representation: 'date' })
              : undefined,
            includeContentFileOnSubmission,
            isDiscoverable,
            targetCountries,
          },
        },
      });

      if (response.data?.updateCampaign) {
        await navigate({
          to: '/campaigns/$id',
          params: {
            id: response.data.updateCampaign.id,
          },
        });
      }
    },
    [id, updateCampaign, navigate],
  );

  const handleOnImageChange = useCallback(
    async (file: File) => {
      const response = await updateCampaignHeaderImage({
        variables: {
          input: {
            id,
            headerImage: file,
          },
        },
      });

      if (response.data?.updateCampaignHeaderImage) {
        enqueueSnackbar('Campaign header image updated', {
          variant: 'success',
        });
      }
    },
    [enqueueSnackbar, id, updateCampaignHeaderImage],
  );

  const handleOnTaskItemTypeChange = useCallback(
    (index: number) => (event: SelectChangeEvent<TaskItemType>) => {
      const value = event.target.value as TaskItemType;
      switch (value) {
        case TaskItemType.FREE_TEXT:
          updateTaskItem(index, {
            type: TaskItemType.FREE_TEXT,
            freeTextTask: {
              description: '',
            },
          });
          break;
        case TaskItemType.WEBSITE:
          updateTaskItem(index, {
            type: TaskItemType.WEBSITE,
            websiteTask: {
              url: '',
            },
          });
          break;
        case TaskItemType.HASHTAG:
          updateTaskItem(index, {
            type: TaskItemType.HASHTAG,
            hashtagTask: {
              hashtags: [],
            },
          });
          break;
        default:
          assertUnreachable(value);
      }
    },
    [updateTaskItem],
  );

  const handleOnRemoveTaskItem = useCallback(
    (index: number) => () => removeTaskItem(index),
    [removeTaskItem],
  );

  const handleOnAddTaskItem = useCallback(() => {
    appendTaskItem({
      type: TaskItemType.FREE_TEXT,
      freeTextTask: {
        description: '',
      },
    });
  }, [appendTaskItem]);

  const handleOnAddGift = useCallback(() => {
    appendGift({
      name: '',
      description: '',
      type: CampaignGiftType.CODE,
    });
  }, [appendGift]);

  const handleOnRemoveGift = useCallback(
    (index: number) => () => {
      removeGift(index);
    },
    [removeGift],
  );

  const handleOnAddQuestion = useCallback(() => {
    appendQuestion({
      question: '',
    });
  }, [appendQuestion]);

  const handleOnRemoveQuestion = useCallback(
    (index: number) => () => {
      removeQuestion(index);
    },
    [removeQuestion],
  );

  const renderTaskItemTypeSelect = useCallback(
    (index: number) =>
      // eslint-disable-next-line react/display-name
      ({
        field: { name, value },
        fieldState: { invalid, error },
        formState: { isSubmitting },
      }: FormFieldProps<
        UpdateCampaignFieldValues,
        `taskItems.${typeof index}.type`
      >) => (
        <FormControl
          sx={{ width: 250 }}
          disabled={isSubmitting}
          error={invalid}>
          <InputLabel error={invalid}>
            {t('manager:form.field.taskItem.type.label')}
          </InputLabel>
          <Select<TaskItemType>
            label={t('manager:form.field.taskItem.type.label')}
            name={name}
            value={value}
            error={invalid}
            onChange={handleOnTaskItemTypeChange(index)}>
            <MenuItem value={TaskItemType.FREE_TEXT}>
              {t('common:taskItemType.freeText')}
            </MenuItem>
            <MenuItem value={TaskItemType.WEBSITE}>
              {t('common:taskItemType.website')}
            </MenuItem>
            <MenuItem value={TaskItemType.HASHTAG}>
              {t('common:taskItemType.hashtag')}
            </MenuItem>
          </Select>
          <FormHelperText error={invalid}>{error?.message}</FormHelperText>
        </FormControl>
      ),
    [handleOnTaskItemTypeChange, t],
  );

  const renderFreeTextTaskItem = useCallback(
    (index: number) =>
      // eslint-disable-next-line react/display-name
      ({
        field: { name, value, onChange, onBlur },
        fieldState: { invalid, error },
        formState: { isSubmitting },
      }: FormFieldProps<
        UpdateCampaignFieldValues,
        `taskItems.${typeof index}.freeTextTask.description`
      >) => (
        <TextField
          fullWidth
          required
          label={t('manager:form.field.freeTextTask.description.label')}
          name={name}
          defaultValue={value}
          disabled={isSubmitting}
          error={invalid}
          helperText={error?.message}
          onChange={onChange}
          onBlur={onBlur}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    disabled={taskItemFields.length <= 1}
                    onClick={handleOnRemoveTaskItem(index)}>
                    <FontAwesomeIcon icon={faXmark} width={24} height={24} />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      ),
    [handleOnRemoveTaskItem, taskItemFields.length, t],
  );

  const renderWebsiteTaskItem = useCallback(
    (index: number) =>
      // eslint-disable-next-line react/display-name
      ({
        field: { name, value, onChange, onBlur },
        fieldState: { invalid, error },
        formState: { isSubmitting },
      }: FormFieldProps<
        UpdateCampaignFieldValues,
        `taskItems.${typeof index}.websiteTask.url`
      >) => (
        <TextField
          fullWidth
          required
          label={t('manager:form.field.websiteTask.url.label')}
          name={name}
          defaultValue={value}
          disabled={isSubmitting}
          error={invalid}
          helperText={error?.message}
          onChange={onChange}
          onBlur={onBlur}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    disabled={taskItemFields.length <= 1}
                    onClick={handleOnRemoveTaskItem(index)}>
                    <FontAwesomeIcon icon={faXmark} width={24} height={24} />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      ),
    [handleOnRemoveTaskItem, taskItemFields.length, t],
  );

  const renderHashtagChips = useCallback(
    ({ error }: { error?: FieldError }) =>
      (hashtags: string[], props: AutocompleteRenderGetTagProps) =>
        hashtags.map((hashtag, hashtagIndex) => {
          return (
            <Chip
              {...props({ index: hashtagIndex })}
              key={
                props({ index: hashtagIndex }).key ||
                `${hashtag}_${hashtagIndex}`
              }
              label={hashtag}
              color={
                error && Array.isArray(error) && error[hashtagIndex]
                  ? 'error'
                  : 'primary'
              }
              size="small"
            />
          );
        }),
    [],
  );

  const renderHashtagInput = useCallback(
    ({
      index,
      name,
      invalid,
      error,
    }: {
      index: number;
      name: string;
      invalid: boolean;
      error?: FieldError;
    }) =>
      // eslint-disable-next-line react/display-name
      ({ InputProps, ...rest }: AutocompleteRenderInputParams) => (
        <TextField
          required
          label={t('manager:form.field.hashtags.label')}
          name={name}
          error={invalid}
          helperText={error?.message}
          {...rest}
          slotProps={{
            input: {
              ...InputProps,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    disabled={taskItemFields.length <= 1}
                    onClick={handleOnRemoveTaskItem(index)}>
                    <FontAwesomeIcon icon={faXmark} width={24} height={24} />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      ),
    [handleOnRemoveTaskItem, taskItemFields.length, t],
  );

  const renderHashtagTaskItem = useCallback(
    (index: number) =>
      // eslint-disable-next-line react/display-name
      ({
        field: { name, value, onChange, onBlur },
        fieldState: { invalid, error },
        formState: { isSubmitting },
      }: FormFieldProps<
        UpdateCampaignFieldValues,
        `taskItems.${typeof index}.hashtagTask.hashtags`
      >) => (
        <Autocomplete<string, true, true, true>
          fullWidth
          options={[]}
          value={value}
          freeSolo
          multiple
          disabled={isSubmitting}
          disableClearable
          renderTags={renderHashtagChips({
            error,
          })}
          renderInput={renderHashtagInput({
            index,
            name,
            invalid,
            error,
          })}
          onChange={onChangeHelper(onChange)}
          onBlur={onBlur}
        />
      ),
    [renderHashtagChips, renderHashtagInput],
  );

  const renderQuestionsField = useCallback(
    (index: number) =>
      // eslint-disable-next-line react/display-name
      ({
        field: { name, value, onChange, onBlur },
        fieldState: { error, invalid },
        formState: { isSubmitting },
      }: FormFieldProps<
        UpdateCampaignFieldValues,
        `questions.${typeof index}.question`
      >) => (
        <TextField
          fullWidth
          required
          label={`${t('manager:form.field.question.label')} ${index + 1}`}
          name={name}
          value={value}
          disabled={isSubmitting}
          error={invalid}
          helperText={error?.message}
          onChange={onChange}
          onBlur={onBlur}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleOnRemoveQuestion(index)}>
                    <FontAwesomeIcon size="sm" icon={faXmark} />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      ),
    [handleOnRemoveQuestion, t],
  );

  const moveItemUp = useCallback(
    (index: number, move: (indexA: number, indexB: number) => void) => () => {
      if (index === 0) {
        return;
      }
      move(index, index - 1);
    },
    [],
  );

  const moveItemDown = useCallback(
    (
      index: number,
      maxIndex: number,
      move: (indexA: number, indexB: number) => void,
    ) =>
      () => {
        if (index === maxIndex) {
          return;
        }
        move(index, index + 1);
      },
    [],
  );

  return (
    <Container
      maxWidth="md"
      component="form"
      noValidate
      onSubmit={handleSubmit(handleOnValid)}>
      <Stack spacing={4}>
        <PageTitle>{t('manager:campaign.update.title')}</PageTitle>
        <Section
          header={t('manager:campaign.form.section.headerImage.header')}
          subHeader={t('manager:campaign.form.section.headerImage.subHeader')}>
          <ImageCropField
            fullWidth
            label={t('manager:campaign.update.headerImage.label')}
            previewSrc={getUriWithHash(data.campaign.headerImage)}
            width={1080}
            height={463}
            onChange={handleOnImageChange}
          />
        </Section>
        <Section
          header={t('manager:campaign.form.section.general.header')}
          subHeader={t('manager:campaign.form.section.general.subHeader')}>
          <Controller<UpdateCampaignFieldValues, 'name'>
            name="name"
            control={control}
            render={CampaignNameField}
          />
          <Controller<UpdateCampaignFieldValues, 'description'>
            name="description"
            control={control}
            render={CampaignDescriptionField}
          />
          <Stack direction="row" spacing={2}>
            <Controller<UpdateCampaignFieldValues, 'type.channelType'>
              name="type.channelType"
              control={control}
              render={ChannelTypeField}
            />
            {watch('type.channelType') === ChannelType.INSTAGRAM ? (
              <Controller<
                UpdateCampaignFieldValues,
                'type.instagramCampaign.contentType'
              >
                name="type.instagramCampaign.contentType"
                control={control}
                render={InstagramContentTypeField}
              />
            ) : null}
            {watch('type.channelType') === ChannelType.YOUTUBE ? (
              <Controller<
                UpdateCampaignFieldValues,
                'type.youtubeCampaign.contentType'
              >
                name="type.youtubeCampaign.contentType"
                control={control}
                render={YoutubeContentTypeField}
              />
            ) : null}
            {watch('type.channelType') === ChannelType.TIKTOK ? (
              <Controller<
                UpdateCampaignFieldValues,
                'type.tiktokCampaign.contentType'
              >
                name="type.tiktokCampaign.contentType"
                control={control}
                render={TiktokContentTypeField}
              />
            ) : null}
          </Stack>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Stack flex={1}>
              <Controller<
                UpdateCampaignFieldValues,
                'includeContentFileOnSubmission'
              >
                name="includeContentFileOnSubmission"
                control={control}
                render={IncludeContentFileOnSubmissionField}
              />
            </Stack>
            <Stack flex={1}>
              <Controller<UpdateCampaignFieldValues, 'isDiscoverable'>
                name="isDiscoverable"
                control={control}
                render={IsDiscoverableField}
              />
            </Stack>
          </Stack>
          <Stack direction="row" spacing={2}>
            <Controller<UpdateCampaignFieldValues, 'brandId'>
              name="brandId"
              control={control}
              render={BrandField}
            />
            <Controller<UpdateCampaignFieldValues, 'category'>
              name="category"
              control={control}
              render={CategoryField}
            />
          </Stack>
        </Section>
        <Section
          header={t('manager:campaign.form.section.tasks.header')}
          subHeader={t('manager:campaign.form.section.tasks.subHeader')}>
          {taskItemFields.map((taskItemField, index) => (
            <Stack key={taskItemField.id} direction="row" spacing={2}>
              <Stack>
                <IconButton
                  size="small"
                  disabled={index === 0}
                  onClick={moveItemUp(index, moveTaskItem)}>
                  <FontAwesomeIcon icon={faChevronUp} />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={index === taskItemFields.length - 1}
                  onClick={moveItemDown(
                    index,
                    taskItemFields.length - 1,
                    moveTaskItem,
                  )}>
                  <FontAwesomeIcon icon={faChevronDown} />
                </IconButton>
              </Stack>
              <Controller<
                UpdateCampaignFieldValues,
                `taskItems.${typeof index}.type`
              >
                name={`taskItems.${index}.type`}
                control={control}
                render={renderTaskItemTypeSelect(index)}
              />
              {taskItemField.type === TaskItemType.FREE_TEXT ? (
                <Controller<
                  UpdateCampaignFieldValues,
                  `taskItems.${typeof index}.freeTextTask.description`
                >
                  name={`taskItems.${index}.freeTextTask.description`}
                  control={control}
                  render={renderFreeTextTaskItem(index)}
                />
              ) : null}
              {taskItemField.type === TaskItemType.WEBSITE ? (
                <Controller<
                  UpdateCampaignFieldValues,
                  `taskItems.${typeof index}.websiteTask.url`
                >
                  name={`taskItems.${index}.websiteTask.url`}
                  control={control}
                  render={renderWebsiteTaskItem(index)}
                />
              ) : null}
              {taskItemField.type === TaskItemType.HASHTAG ? (
                <Controller<
                  UpdateCampaignFieldValues,
                  `taskItems.${typeof index}.hashtagTask.hashtags`
                >
                  name={`taskItems.${index}.hashtagTask.hashtags`}
                  control={control}
                  render={renderHashtagTaskItem(index)}
                />
              ) : null}
            </Stack>
          ))}
          <Button
            variant="outlined"
            startIcon={<FontAwesomeIcon icon={faPlus} />}
            disabled={taskItemFields.length >= 10}
            onClick={handleOnAddTaskItem}>
            {t('manager:form.field.task.addButton')}
          </Button>
        </Section>
        <Section
          header={t('manager:campaign.form.section.reward.header')}
          subHeader={t('manager:campaign.form.section.reward.subHeader')}>
          <Stack direction="row" spacing={2}>
            <Controller<UpdateCampaignFieldValues, 'reward.rewardType'>
              name="reward.rewardType"
              control={control}
              render={RewardTypeField}
            />
            {watch('reward.rewardType') ===
            CampaignRewardType.FIXED_MONEY_REWARD ? (
              <Controller<
                UpdateCampaignFieldValues,
                'reward.fixedMoneyRewardValue'
              >
                name="reward.fixedMoneyRewardValue"
                control={control}
                render={FixedMoneyRewardField}
              />
            ) : null}
          </Stack>
        </Section>
        <Section
          header={t('manager:campaign.form.section.targetCountries.header')}
          subHeader={t(
            'manager:campaign.form.section.targetCountries.subHeader',
          )}>
          <Controller<UpdateCampaignFieldValues, 'targetCountries'>
            name="targetCountries"
            control={control}
            render={TargetCountriesField}
          />
        </Section>
        <Divider>
          <Chip
            variant="outlined"
            label={t('manager:campaign.form.section.optional.header')}
          />
        </Divider>
        <Section
          header={t('manager:campaign.form.section.deadline.header')}
          subHeader={t('manager:campaign.form.section.deadline.subHeader')}>
          <Controller<UpdateCampaignFieldValues, 'applyUntil'>
            name="applyUntil"
            control={control}
            render={ApplyUntilField}
          />
        </Section>
        <Section
          header={t('manager:campaign.form.section.location.header')}
          subHeader={t('manager:campaign.form.section.location.subHeader')}>
          <Controller<UpdateCampaignFieldValues, 'address'>
            name="address"
            control={control}
            render={AddressField}
          />
        </Section>
        <Section
          header={t('manager:campaign.form.section.gift.header')}
          subHeader={t('manager:campaign.form.section.gift.subHeader')}>
          {giftFields.map((field, index) => (
            <Stack key={field.id} spacing={2}>
              <Controller<
                UpdateCampaignFieldValues,
                `gifts.${typeof index}.type`
              >
                name={`gifts.${index}.type`}
                control={control}
                render={GiftTypeField}
              />
              <Controller<
                UpdateCampaignFieldValues,
                `gifts.${typeof index}.name`
              >
                name={`gifts.${index}.name`}
                control={control}
                render={GiftNameField}
              />
              <Controller<
                UpdateCampaignFieldValues,
                `gifts.${typeof index}.description`
              >
                name={`gifts.${index}.description`}
                control={control}
                render={GiftDescriptionField}
              />
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<FontAwesomeIcon icon={faXmark} />}
                onClick={handleOnRemoveGift(index)}>
                {t('manager:form.field.gift.removeButton')}
              </Button>
              <Divider />
            </Stack>
          ))}
          <Button
            variant="outlined"
            startIcon={<FontAwesomeIcon icon={faPlus} />}
            disabled={giftFields.length >= 5}
            onClick={handleOnAddGift}>
            {t('manager:form.field.gift.addButton')}
          </Button>
        </Section>
        <Section
          header={t('manager:campaign.form.section.questions.header')}
          subHeader={t('manager:campaign.form.section.questions.subHeader')}>
          {questionFields.map((field, index) => (
            <Stack key={field.id} direction="row" spacing={2}>
              <Stack>
                <IconButton
                  size="small"
                  disabled={index === 0}
                  onClick={moveItemUp(index, moveQuestion)}>
                  <FontAwesomeIcon icon={faChevronUp} />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={index === questionFields.length - 1}
                  onClick={moveItemDown(
                    index,
                    questionFields.length - 1,
                    moveQuestion,
                  )}>
                  <FontAwesomeIcon icon={faChevronDown} />
                </IconButton>
              </Stack>
              <Controller<
                UpdateCampaignFieldValues,
                `questions.${typeof index}.question`
              >
                name={`questions.${index}.question`}
                control={control}
                render={renderQuestionsField(index)}
              />
            </Stack>
          ))}
          <Button
            variant="outlined"
            startIcon={<FontAwesomeIcon icon={faPlus} />}
            disabled={questionFields.length >= 5}
            onClick={handleOnAddQuestion}>
            {t('manager:form.field.questions.addButton')}
          </Button>
        </Section>
        <Button
          size="large"
          type="submit"
          variant="contained"
          loading={updateCampaignLoading}
          disabled={!isValid}>
          {t('manager:campaign.update.submitButton')}
        </Button>
      </Stack>
    </Container>
  );
}
